/**
 * PATH: apps/qa/src/pages/TemplateDetailPage.tsx
 * PURPOSE: View and edit QA checklist templates
 *
 * DEPENDENCIES:
 *   - ./template-detail/*: extracted sortable components and types
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { templateApi } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ArrowLeft, Save, FileText, Plus, AlertTriangle, Loader2, Search, Globe2, Building2, Lock } from 'lucide-react'
import type { TemplateSection, TemplateItem } from './template-detail/types'
import { generateLocalId, getSectionId } from './template-detail/types'
import { SortableSection } from './template-detail/SortableSection'
import { SourceCoveragePanel } from './template-detail/SourceCoveragePanel'
import { itemMatches, normaliseItem, parseTags, persistExistingStructure, serialiseItem } from './template-detail/template-persistence'
import { useQaTabStore } from '../config/tabs'

export default function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const updateActiveTab = useQaTabStore(s => s.updateActiveTab)
  const isNew = !id || id === 'new'

  const [templateName, setTemplateName] = useState('')
  const [trade, setTrade] = useState('')
  const [system, setSystem] = useState('')
  const [tags, setTags] = useState('')
  const [findQuery, setFindQuery] = useState('')
  const [sections, setSections] = useState<TemplateSection[]>([])
  const [originalSections, setOriginalSections] = useState<TemplateSection[]>([])
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]))
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const { data: template, isLoading, error } = useQuery({
    queryKey: ['qa-template', id], queryFn: () => templateApi.getTemplate(id!), enabled: !isNew && !!id,
  })

  useEffect(() => {
    if (template?.name) {
      updateActiveTab(location.pathname, template.name)
    }
  }, [template?.name, location.pathname, updateActiveTab])

  useEffect(() => {
    if (template) {
      setTemplateName(template.name || ''); setTrade(template.trade || ''); setSystem(template.system || ''); setTags((template.tags || []).join(', '))
      const loadedSections = template.sections?.map((s: { id: string; title: string; section_number: string; description?: string; sort_order: number; items?: TemplateItem[] }, idx: number) => ({
        ...s, title: s.title || `Section ${idx + 1}`, section_number: s.section_number || `${idx + 1}`, sort_order: s.sort_order ?? idx,
        items: s.items?.map((item: TemplateItem, itemIdx: number) => normaliseItem(item, `${idx + 1}.${itemIdx + 1}`)) || [],
      })) || []
      setSections(loadedSections)
      setOriginalSections(loadedSections)
      setHasUnsavedChanges(false)
    }
  }, [template])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const metadata = { name: templateName, trade, system, tags: parseTags(tags) }
      if (isNew) {
        return templateApi.createTemplate({
          ...metadata,
          sections: sections.map((section, sectionIndex) => ({
            title: section.title, section_number: section.section_number || `${sectionIndex + 1}`,
            description: section.description || '', sort_order: sectionIndex,
            items: section.items.map((item, itemIndex) => serialiseItem(item, `${sectionIndex + 1}.${itemIndex + 1}`)),
          })),
        } as Parameters<typeof templateApi.createTemplate>[0])
      }
      await templateApi.updateTemplate(id!, metadata)
      await persistExistingStructure(id!, sections, originalSections)
      return templateApi.getTemplate(id!)
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['qa-templates'] })
      queryClient.setQueryData(['qa-template', saved.id], saved)
      setHasUnsavedChanges(false)
      if (isNew) navigate(`/templates/${saved.id}`)
    },
  })

  const handleSave = () => saveMutation.mutate()
  const addSection = () => { setSections(p => [...p, { _localId: generateLocalId(), title: `Section ${p.length + 1}`, section_number: `${p.length + 1}`, sort_order: p.length, items: [] }]); setExpandedSections(p => new Set([...p, sections.length])); setHasUnsavedChanges(true) }
  const removeSection = (i: number) => { setSections(p => p.filter((_, j) => j !== i)); setHasUnsavedChanges(true) }
  const updateSection = (i: number, u: Partial<TemplateSection>) => { setSections(p => p.map((s, j) => j === i ? { ...s, ...u } : s)); setHasUnsavedChanges(true) }

  const addItem = (si: number) => {
    setSections(p => p.map((s, i) => { if (i !== si) return s; const n = s.section_number || `${i + 1}`; return { ...s, items: [...s.items, normaliseItem({ _localId: generateLocalId(), title: `Item ${s.items.length + 1}`, item_number: `${n}.${s.items.length + 1}` }, `${n}.${s.items.length + 1}`)] } })); setHasUnsavedChanges(true)
  }
  const updateItem = (si: number, ii: number, u: Partial<TemplateItem>) => { setSections(p => p.map((s, i) => i !== si ? s : { ...s, items: s.items.map((item, j) => j === ii ? { ...item, ...u } : item) })); setHasUnsavedChanges(true) }
  const removeItem = (si: number, ii: number) => { setSections(p => p.map((s, i) => i !== si ? s : { ...s, items: s.items.filter((_, j) => j !== ii) })); setHasUnsavedChanges(true) }
  const toggleSection = (i: number) => setExpandedSections(p => { const n = new Set(p); if (n.has(i)) n.delete(i); else n.add(i); return n })

  const reorderSections = useCallback((o: number, n: number) => { setSections(p => arrayMove(p, o, n).map((s, i) => ({ ...s, sort_order: i }))); setHasUnsavedChanges(true) }, [])
  const reorderItems = useCallback((si: number, o: number, n: number) => {
    setSections(p => p.map((s, i) => i !== si ? s : { ...s, items: arrayMove(s.items, o, n).map((item, idx) => ({ ...item, item_number: `${s.section_number || i + 1}.${idx + 1}` })) })); setHasUnsavedChanges(true)
  }, [])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }))
  const sectionIds = sections.map((s, i) => getSectionId(s, i))
  const handleSectionDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event; if (over && active.id !== over.id) { const o = sectionIds.indexOf(String(active.id)); const n = sectionIds.indexOf(String(over.id)); if (o !== -1 && n !== -1) reorderSections(o, n) }
  }, [sectionIds, reorderSections])

  const isPending = saveMutation.isPending
  const isGlobal = !isNew && template?.organisation === null
  const isImmutable = isGlobal || template?.status === 'published' || template?.status === 'archived'
  const matchingItems = findQuery.trim()
    ? sections.reduce((total, section) => total + section.items.filter((item) => itemMatches(item, findQuery)).length, 0)
    : 0

  if (!isNew && isLoading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
  if (!isNew && error) return (
    <div className="text-center py-16"><div className="w-16 h-16 rounded-2xl bg-destructive/10 mx-auto mb-4 flex items-center justify-center"><AlertTriangle className="w-8 h-8 text-destructive" /></div>
      <p className="text-foreground font-medium">Template not found</p><Link to="/templates" className="mt-4 inline-flex items-center gap-2 text-primary hover:text-primary-600 transition-colors"><ArrowLeft className="w-4 h-4" />Back to templates</Link></div>
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/templates')} className="p-2 rounded-lg hover:bg-accent transition-colors"><ArrowLeft className="w-5 h-5 text-muted-foreground" /></button>
          <div><h1 className="text-2xl font-bold text-foreground">{isNew ? 'New Template' : isImmutable ? 'Template details' : 'Edit Template'}</h1>
            {!isNew && template?.created_at && <p className="text-muted-foreground text-sm">Created {formatDateTime(template.created_at)}</p>}</div>
        </div>
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && <span className="text-sm text-amber-600 dark:text-amber-400">Unsaved changes</span>}
          {!isImmutable && <button onClick={handleSave} disabled={isPending || !templateName} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{isNew ? 'Create Template' : 'Save Changes'}
          </button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" aria-label="Template lifecycle">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground">
          {isGlobal ? <Globe2 className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}{isGlobal ? 'Quotech global' : 'Organisation'}
        </span>
        {!isNew && <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-foreground">{template?.status}</span>}
        {!isNew && <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">Version {template?.version}</span>}
        {isImmutable && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800"><Lock className="h-3.5 w-3.5" />Immutable</span>}
        <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">Job copy: created only when applied</span>
      </div>

      {saveMutation.isError && <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">Template save failed. Existing content was not marked saved; retry after checking your permissions.</div>}

      <fieldset disabled={isImmutable} className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-4 disabled:opacity-80">
        <div><label className="block text-sm font-medium text-foreground mb-2">Template Name <span className="text-destructive">*</span></label>
          <input type="text" value={templateName} onChange={(e) => { setTemplateName(e.target.value); setHasUnsavedChanges(true) }} placeholder="e.g., Fire Safety Inspection" className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" /></div>
        <div className="grid gap-4 md:grid-cols-3"><div><label className="block text-sm font-medium text-foreground mb-2">Trade</label>
          <select value={trade} onChange={(e) => { setTrade(e.target.value); setHasUnsavedChanges(true) }} className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
            <option value="">Select Trade</option><option value="general">General</option><option value="waterproofing">Waterproofing</option><option value="electrical">Electrical</option><option value="plumbing">Plumbing</option><option value="structural">Structural</option><option value="finishing">Finishing</option><option value="fire_safety">Fire Safety</option><option value="hvac">HVAC</option><option value="roofing">Roofing</option>
          </select></div>
          <div><label className="block text-sm font-medium text-foreground mb-2">System / category</label><input value={system} onChange={(e) => { setSystem(e.target.value); setHasUnsavedChanges(true) }} className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground" /></div>
          <div><label className="block text-sm font-medium text-foreground mb-2">Tags</label><input value={tags} onChange={(e) => { setTags(e.target.value); setHasUnsavedChanges(true) }} placeholder="comma, separated" className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground" /></div>
        </div>
      </fieldset>

      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input type="search" value={findQuery} onChange={(event) => setFindQuery(event.target.value)} placeholder="Find requirement, acceptance criteria, citation..."
          className="w-full rounded-xl border border-input bg-background py-2.5 pl-10 pr-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20" />
        {findQuery && <span className="mt-1 block text-xs text-muted-foreground" role="status">{matchingItems} matching item{matchingItems === 1 ? '' : 's'}</span>}
      </div>

      <div className={`space-y-4 ${isImmutable ? 'pointer-events-none' : ''}`} aria-readonly={isImmutable}>
        <div className="flex items-center justify-between"><h2 className="text-lg font-semibold text-foreground">Sections</h2>
          {!isImmutable && <button onClick={addSection} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-input text-foreground text-sm font-medium hover:bg-accent transition-colors"><Plus className="w-4 h-4" />Add Section</button>}</div>
        {sections.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border border-dashed border-border">
            <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground" /><p className="text-foreground font-medium">No sections yet</p><p className="text-sm text-muted-foreground mt-1">Add sections to organize your checklist items</p>
            <button onClick={addSection} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors"><Plus className="w-4 h-4" />Add First Section</button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
            <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {sections.map((section, i) => (
                  <div key={getSectionId(section, i)} className={findQuery && section.items.some((item) => itemMatches(item, findQuery)) ? 'ring-2 ring-primary/30 rounded-xl' : ''}>
                  <SortableSection section={section} sectionIndex={i} isExpanded={expandedSections.has(i)}
                    onToggle={() => toggleSection(i)} onUpdate={(u) => updateSection(i, u)} onRemove={() => removeSection(i)}
                    onAddItem={() => addItem(i)} onUpdateItem={(ii, u) => updateItem(i, ii, u)} onRemoveItem={(ii) => removeItem(i, ii)} onReorderItems={(o, n) => reorderItems(i, o, n)} />
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
      <SourceCoveragePanel templateId={template?.id} templateStatus={template?.status} isGlobal={isGlobal} sections={sections} />
    </div>
  )
}
